import {
  cloneElement,
  type FocusEvent,
  type KeyboardEvent,
  type ReactElement,
  type PointerEvent as ReactPointerEvent,
  type TextareaHTMLAttributes,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { IconButton } from "../IconButton/IconButton";

const MAX_TEXTAREA_HEIGHT = 128;

export interface CompactComposerProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "className" | "placeholder" | "rows"> {
  placeholder: string;

  leadingIcon: ReactElement<{ size?: number }>;

  sendIcon: ReactElement<{ size?: number }>;

  /** Fires from the send button, and from Enter (Shift+Enter adds a line). */
  onSend?: () => void;

  sendDisabled?: boolean;
}

export function CompactComposer({
  leadingIcon,
  sendIcon,
  onSend,
  sendDisabled = false,
  disabled,
  value,
  onInput,
  onKeyDown,
  onPointerDown,
  onFocus,
  onBlur,
  ...rest
}: Readonly<CompactComposerProps>) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pointerDownRef = useRef(false);
  const [focusMethod, setFocusMethod] = useState<"pointer" | "keyboard" | null>(null);
  const isKeyboardFocus = focusMethod === "keyboard";
  const isFocused = focusMethod !== null;

  const resize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
    el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_HEIGHT ? "auto" : "hidden";
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately watches value to recalc height on every change, not reading it inside resize.
  useLayoutEffect(resize, [value]);

  // React 18 types onInput as a FormEvent and React 19 as an InputEvent: take whichever this React declares.
  const handleInput = (event: Parameters<NonNullable<CompactComposerProps["onInput"]>>[0]) => {
    resize();
    onInput?.(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (!disabled && !sendDisabled) onSend?.();
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLTextAreaElement>) => {
    pointerDownRef.current = true;
    onPointerDown?.(event);
  };

  const handleFocus = (event: FocusEvent<HTMLTextAreaElement>) => {
    setFocusMethod(pointerDownRef.current ? "pointer" : "keyboard");
    pointerDownRef.current = false;
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
    setFocusMethod(null);
    onBlur?.(event);
  };

  return (
    <div className="relative isolate w-full min-w-0 flex-1">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute top-6 left-4 z-10 inline-flex -translate-y-1/2 items-center justify-center text-content-tertiary"
      >
        {cloneElement(leadingIcon, { size: 16 })}
      </span>
      <textarea
        {...rest}
        ref={textareaRef}
        rows={1}
        value={value}
        disabled={disabled}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={[
          "block min-h-12 w-full resize-none rounded-xl border py-3.5 pr-14 pl-10 text-sm leading-5 text-content-primary shadow-sm outline-hidden transition-colors placeholder:text-content-tertiary disabled:cursor-not-allowed disabled:opacity-50",
          isFocused
            ? "border-brand-gradient-warm-vertical"
            : "bg-surface-elevated border-border-default hover:border-border-strong",
          isKeyboardFocus ? "input-focus-ring" : "",
        ].join(" ")}
      />
      <span className="absolute right-2 bottom-2 z-10 inline-flex items-center justify-center">
        <IconButton
          variant="solid"
          size="md"
          aria-label="Send"
          icon={sendIcon}
          onClick={onSend}
          disabled={disabled || sendDisabled}
        />
      </span>
    </div>
  );
}
